import React, { useEffect } from 'react'

import { faTrash } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Stack, SxProps, TextField, Typography, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import { defaults } from 'lodash-es'
import { DropzoneOptions, FileRejection, FileWithPath, useDropzone } from 'react-dropzone'

import FilesPreview from './components/FilePreview/FilesPreview'
import RejectedFiles from './components/RejectedFiles.tsx/RejectedFiles'
import { getErrorList } from './FileUploader.utils'

const DropzoneWrapper = styled('div')(({ theme }) => ({
	outline: 'none',
	borderRadius: theme.shape.borderRadius,
	backgroundColor: theme.palette.background.paper,
	border: `2px dashed ${theme.palette.info.light}`,
	'&:hover': { opacity: 0.65, cursor: 'pointer' },
}))

type BaseProps = {
	label?: string
	placeholder?: string
	files?: FileWithPath[] // files that will be displayed
	onUploadError?: (error: FileRejection) => void
	dropzoneOptions?: DropzoneOptions // see https://react-dropzone.js.org/#src
	allowedDescription?: string // optional description of allowed files
	onChange: (files: FileWithPath[]) => void
	previouslyUploadedFiles?: string[]
	error?: boolean // can display errors for dragged files
	sx?: SxProps
}

type SmallVariantProps = BaseProps & {
	variant: 'small'
	multiple?: boolean
}

type LargeVariantProps = BaseProps & {
	variant: 'large'
	multiple?: boolean
}

type Props = SmallVariantProps | LargeVariantProps

/**
 * Drag and drop file uploader. This is a controlled component.
 * The parent is responsible for managing the files state.
 */
export default function FileUploader({
	variant = 'small',
	placeholder = null,
	label = null,
	error = null,
	files = null,
	multiple = false,
	onChange,
	onUploadError = null,
	allowedDescription = null,
	dropzoneOptions = {},
	previouslyUploadedFiles = null,
	sx = {},
}: Props) {
	const options = defaults(dropzoneOptions, {
		maxFiles: multiple ? null : 1,
		maxSize: 5242880, // 5MB
	})

	const theme = useTheme()

	const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
		multiple,
		...options,
		onDrop: (acceptedFiles) => {
			onChange(files && multiple ? [...files, ...acceptedFiles] : acceptedFiles)
		},
	})

	useEffect(() => {
		if (onUploadError && fileRejections.length) onUploadError(fileRejections[0])
	}, [fileRejections, onUploadError])

	const onRemove = (file: File) => {
		if (variant === 'small') return onChange(null)
		const filteredItems = files && files.filter((_file) => _file !== file)
		onChange(filteredItems)
	}

	if (variant === 'small')
		return (
			<Box {...getRootProps()} sx={{ width: '100%', ...sx }}>
				<input {...getInputProps()} style={{ display: 'none' }} />
				<TextField
					sx={{
						input: {
							cursor: 'pointer',
						},
						'& .MuiOutlinedInput-root': {
							'& fieldset': {
								border: `2px dashed ${theme.palette.info.light}`,
							},
							'&:hover fieldset': {
								borderColor: theme.palette.info.light,
								opacity: 0.65,
							},
						},
					}}
					variant="outlined"
					fullWidth
					value={files?.length && files[0] ? files[0].name : ''}
					placeholder={placeholder}
					error={fileRejections.length > 0 || error}
					helperText={
						fileRejections.length && !onUploadError ? getErrorList(fileRejections[0]) : null
					}
					InputProps={{
						readOnly: true,
						endAdornment: files?.length ? (
							<FontAwesomeIcon
								style={{ cursor: 'pointer' }}
								onClick={(event: React.MouseEvent) => {
									event.stopPropagation()
									onRemove(files[0])
								}}
								icon={faTrash}
							/>
						) : null,
					}}
				/>
			</Box>
		)

	return (
		<Stack spacing={2} sx={{ width: '100%', height: '100%', ...sx }}>
			{label && (
				<Typography variant="body1" color={error ? 'error.main' : 'text.primary'}>
					{label}
				</Typography>
			)}

			<DropzoneWrapper
				{...getRootProps()}
				sx={{
					...(isDragActive && { opacity: 0.65 }),
					...((isDragReject || fileRejections.length > 0 || error) && {
						color: 'error.main',
						borderColor: 'error.light',
						bgcolor: 'error.lighter',
					}),
				}}
			>
				<input {...getInputProps()} style={{ display: 'none' }} />
				<Stack
					spacing={2}
					alignItems="center"
					justifyContent="center"
					direction={{ xs: 'column', sm: 'row' }}
					sx={{ width: 1, textAlign: { xs: 'center', sm: 'left' } }}
				>
					<Stack alignItems="center" sx={{ p: 2 }} spacing={1}>
						<Typography variant="body2">
							Drag & Drop File{multiple ? 's' : ''} Here or Click Select File{multiple ? 's' : ''}
						</Typography>
						<Box py={0.5} px={1} sx={{ backgroundColor: 'primary.main', borderRadius: 1 }}>
							<Typography variant="caption" color="common.white">
								SELECT FILE{multiple ? 'S' : ''}
							</Typography>
						</Box>
						{allowedDescription && (
							<Typography variant="body2" color="secondary" sx={{ mt: 1 }}>
								{allowedDescription}
							</Typography>
						)}
					</Stack>
				</Stack>
			</DropzoneWrapper>
			{fileRejections.length > 0 && <RejectedFiles fileRejections={fileRejections} />}
			{files?.length > 0 && <FilesPreview files={files} onRemove={onRemove} />}
			{!files?.length && previouslyUploadedFiles?.length > 0 && (
				<Stack>
					<Typography variant="body2" color="textSecondary">
						Previously uploaded file{previouslyUploadedFiles.length > 1 ? 's' : ''} (uploading a new
						file will replace these):
					</Typography>
					<FilesPreview files={previouslyUploadedFiles} />
				</Stack>
			)}
		</Stack>
	)
}
